import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Offcanvas } from 'react-bootstrap';
import { createUseThemedStyles, useAccount, useSignOut } from '@/hooks';
import { HeaderMenuAccordion, HeaderSearch, NavigationLinks } from '@/components';
import { ReactComponent as HackettLogo } from '@/assets/logos/logo-hackett-connect-r-large.svg';
import { ReactComponent as XIcon } from '@/assets/icons/icon-x.svg';

const useStyles = createUseThemedStyles((theme) => ({
	header: {
		height: 60,
		display: 'flex',
		padding: '0 20px',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: theme.colors.info,
		'& .hackett-logo': {
			width: 233,
			display: 'block',
			color: theme.colors.white,
		},
	},
	body: {
		overflowY: 'auto',
		height: 'calc(100% - 60px)',
	},
	mobileSearchBar: {
		display: 'flex',
		padding: '16px 20px',
		justifyContent: 'center',
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	navigationList: {
		margin: 0,
		padding: 0,
		listStyle: 'none',
		'& li': {
			borderBottom: `1px solid ${theme.colors.gray300}`,
			'& a, & button': {
				width: '100%',
				display: 'block',
				textAlign: 'left',
				padding: '16px 24px',
				'&:last-of-type': {
					margin: 0,
				},
			},
		},
	},
	navigationButtons: {
		padding: 24,
		borderBottom: `1px solid ${theme.colors.gray300}`,
		'& a': {
			marginBottom: 8,
			'&:last-of-type': {
				margin: 0,
			},
		},
	},
	'@global': {
		'.mobile-nav-enter': {
			opacity: 0,
			transform: 'translateY(50px)',
		},
		'.mobile-nav-enter-active': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-enter-done': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-exit': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-exit-active': {
			opacity: 0,
			transform: 'translateY(50px)',
		},
	},
}));

interface HeaderMenuProps {
	show: boolean;
	onHide(): void;
	navigationLinks: NavigationLinks;
}

export const HeaderMenu = ({ show, onHide, navigationLinks }: HeaderMenuProps) => {
	const classes = useStyles();
	const { account } = useAccount();
	const { signOut } = useSignOut();

	return (
		<Offcanvas show={show} onHide={onHide} placement="end">
			<div className={classes.header}>
				<Link to="/">
					<HackettLogo className="hackett-logo" />
				</Link>
				<Button variant="info" className="p-2 text-white" onClick={onHide}>
					<XIcon />
				</Button>
			</div>
			<div className={classes.body}>
				<div className={classes.mobileSearchBar}>
					<HeaderSearch className="w-100" />
				</div>
				{Object.entries(navigationLinks).map(([key, section]) => {
					if (key === 'SPEND_MATTERS') {
						if (section.length === 0) {
							return null;
						}

						return (
							<React.Fragment key={key}>
								{section.length > 0 && <HeaderMenuAccordion title="Solutions" links={section} />}
							</React.Fragment>
						);
					}

					if (section.length <= 0) {
						return null;
					}

					if (key === 'CLIENT_MANAGEMENT') {
						return <HeaderMenuAccordion key={key} title="Client Management" links={section} />;
					}

					if (key === 'ACCOUNT') {
						return (
							<HeaderMenuAccordion
								key={key}
								title={account?.name ?? ''}
								links={section}
								aside={
									<Button variant="link" className="mt-2" onClick={signOut}>
										Sign Out
									</Button>
								}
							/>
						);
					}

					return (
						<React.Fragment key={key}>
							<ul className={classes.navigationList}>
								{section.map((link, linkIndex) => {
									if (link.external) {
										return (
											<li key={linkIndex}>
												<a
													href={link.to}
													target="_blank"
													rel="noreferrer"
													onClick={link.onClick}
													dangerouslySetInnerHTML={{ __html: link.mobileTitle ?? link.title }}
												/>
											</li>
										);
									} else {
										return (
											<li key={linkIndex}>
												<Link
													to={link.to}
													onClick={link.onClick}
													dangerouslySetInnerHTML={{ __html: link.mobileTitle ?? link.title }}
												/>
											</li>
										);
									}
								})}
							</ul>
						</React.Fragment>
					);
				})}
				<div className={classes.navigationButtons}>
					<Button
						variant="info"
						size="sm"
						className="d-block w-100 text-nowrap text-center"
						href="/advisory-team"
					>
						My Advisors
					</Button>
					<Button
						size="sm"
						className="d-block w-100 text-nowrap text-center"
						variant="secondary"
						href="/contact"
					>
						Contact Us
					</Button>
				</div>
			</div>
		</Offcanvas>
	);
};
